import Konva from 'konva';
import { attachInteractive, pointerPos } from './_helpers.js';

// Bug-pin marker — a "frozen ping" on the screenshot. Three nested filled
// mint discs at low alpha stack into soft concentric bands; a flat solid
// mint disc at the center is the marker. Nothing else — no gradient, no
// outline, no shadow. The reference design is meant to read as a single
// hue with stepped translucency, and any "depth" trick fights that.
// ~84px outer footprint in a 96×96 viewBox.
const PIN_SVG = 'data:image/svg+xml;base64,' + btoa(
  '<svg width="96" height="96" viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">' +
    '<circle cx="48" cy="48" r="42" fill="#10E89C" fill-opacity="0.10"/>' +
    '<circle cx="48" cy="48" r="30" fill="#10E89C" fill-opacity="0.16"/>' +
    '<circle cx="48" cy="48" r="18" fill="#10E89C" fill-opacity="0.24"/>' +
    '<circle cx="48" cy="48" r="7.5" fill="#10E89C"/>' +
  '</svg>'
);

const PIN_NAME = 'bug-pin-marker';

let pinImg = null;
let pinReady = null;

// Eagerly start loading the pin sprite. By the time the user picks up the
// bug-pin tool, the SVG has almost always finished decoding — but we keep a
// Promise too, since the auto-advance flow exports the screenshot the moment
// the user clicks. Without awaiting, the JPEG races past the load and the
// pin is missing from the saved screenshot.
function ensurePin() {
  if (pinReady) return pinReady;
  pinImg = new Image();
  pinReady = new Promise((resolve) => {
    const finish = () => resolve(pinImg);
    pinImg.addEventListener('load', finish, { once: true });
    pinImg.addEventListener('error', finish, { once: true });
    pinImg.src = PIN_SVG;
    // Already cached or instantly decoded — guard against the listeners
    // never firing because the image was already complete by the time we
    // attached them.
    if (pinImg.complete && pinImg.naturalWidth > 0) finish();
  });
  return pinReady;
}

// Kick off load at module init so the sprite is ready well before first use.
ensurePin();

// Returns the user's click in coordinate systems that an AI agent can use:
//   source       — pixels relative to the original screenshot bitmap
//                  (e.g. {x: 482, y: 311} on a 1920x1200 screenshot)
//   normalized   — fraction of the screenshot region in [0..1], so coords
//                  survive any downstream resizing of the JPEG
//   source_dims  — the source bitmap dimensions, so an AI knows what the
//                  source-pixel coords are anchored to
function computePinCoords(engine, pos) {
  const layout = engine.layout;
  const localX = pos.x - layout.ox;
  const localY = pos.y - layout.oy;
  const sxMul = layout.naturalW && layout.dw ? layout.naturalW / layout.dw : 1;
  const syMul = layout.naturalH && layout.dh ? layout.naturalH / layout.dh : 1;
  const round4 = (v) => Math.round(v * 10000) / 10000;
  return {
    source: {
      x: Math.round(localX * sxMul),
      y: Math.round(localY * syMul),
    },
    normalized: {
      x: layout.dw ? round4(localX / layout.dw) : 0,
      y: layout.dh ? round4(localY / layout.dh) : 0,
    },
    source_dimensions: {
      width: layout.naturalW || 0,
      height: layout.naturalH || 0,
    },
  };
}

export const bugPin = {
  id: 'bug-pin',
  defaultProps: {},
  async onPointerDown(engine) {
    const pos = pointerPos(engine);
    if (!engine.pointerInsideImage(pos)) return;

    // Single pin only — drop any previous marker so each click replaces
    // (rather than stacks) the issue location.
    const existing = engine.contentLayer.find(`.${PIN_NAME}`);
    existing.forEach((p) => p.destroy());

    // Wait for the sprite to finish decoding before signaling placement.
    // The host UI listens for `bug-pin-placed` and immediately exports the
    // composite — without this await the JPEG would be exported before the
    // pin's pixels are rendered into the canvas.
    const img = await ensurePin();

    // 96×96 SVG. Outermost band sits at r=42; the rest of the viewBox is
    // breathing room. Center the SVG on the click so the pin's optical
    // center (the mint core) lands on the exact pixel the user picked —
    // the AI agent's downstream coords describe that point.
    const node = new Konva.Image({
      image: img,
      x: pos.x - 48,
      y: pos.y - 48,
      width: 96,
      height: 96,
      name: PIN_NAME,
    });
    attachInteractive(engine, node);
    engine.contentLayer.batchDraw();

    const coords = computePinCoords(engine, pos);
    engine.setPinCoordinates(coords);
    engine.emit('bug-pin-placed', coords);
  },
  onPointerMove() {},
  onPointerUp() {},
};
